<script>
  import { onMount } from "svelte";
  import { get } from "svelte/store";
  import {
    savedCalculations,
    showToast,
    getDARate,
    saveCalculation,
  } from "./stores.js";
  import CalculationForm from "./CalculationForm.svelte";
  import ResultsView from "./ResultsView.svelte";

  export let calculationId = null;
  export let onNavigate;

  // This is the single source of truth for the current calculation being worked on.
  let calculation = null;

  onMount(() => {
    if (calculationId) {
      const existingCalc = get(savedCalculations).find(
        (c) => c.id === calculationId
      );
      if (existingCalc) {
        // IMPORTANT: Deep copy the calculation so we don't accidentally modify the global store.
        calculation = JSON.parse(JSON.stringify(existingCalc));
      }
    }
    // If no ID or not found, `calculation` remains null, so the form is fresh.
  });

  function handleCalculate(event) {
    try {
      const formData = event.detail;

      // Validate required fields
      if (!formData.startDate || !formData.endDate || !formData.basicPay) {
        showToast("Please fill in all required fields (start date, end date, basic pay)", "error");
        return;
      }

      // Smart DA Source switching check
      if (
        calculation &&
        calculation.daSource !== "manual" &&
        formData.daSource === "manual"
      ) {
        // User is switching TO manual. The table will preserve the current values.
        console.log("Switching to manual mode - preserving existing DA values");
      } else if (calculation && calculation.daSource !== formData.daSource) {
        if (
          !confirm(
            `This will recalculate all DA rates based on the new source. Are you sure?`
          )
        ) {
          return; // Abort if user cancels
        }
      }

      const { startDate, endDate, basicPay, salaryType, daSource, commutation } =
        formData;

      let newTableData = [];
      let currentDate = new Date(startDate + "-01T12:00:00Z");
      const endMonth = new Date(endDate + "-01T12:00:00Z");

      // Validate date range
      if (currentDate > endMonth) {
        showToast("Start date cannot be after end date", "error");
        return;
      }

      console.log(`Generating table from ${startDate} to ${endDate} with DA source: ${daSource}`);

      while (currentDate <= endMonth) {
        const monthYear = currentDate.toLocaleString("default", {
          month: "long",
          year: "numeric",
          timeZone: "UTC",
        });
        
        // Find existing row to preserve user edits
        const existingRow = calculation?.tableData?.find(
          (r) => r.monthYear === monthYear
        );

        // Logic to get DA rate correctly
        let daRate = 0;
        if (daSource === "manual") {
          // Preserve existing DA rate if available, otherwise use 0
          daRate = existingRow?.daRate || 0;
        } else {
          // Get DA rate from the selected source
          daRate = getDARate(currentDate, daSource, salaryType);
          console.log(`DA rate for ${monthYear}: ${daRate}% (source: ${daSource})`);
        }

        // Calculate commuted amount for this month if applicable
        let commutedAmount = 0;
        if (commutation?.opted && commutation?.amount) {
          const commutationStart = new Date(commutation.startDate + "-01T12:00:00Z");
          const commutationEnd = new Date(commutation.endDate + "-01T12:00:00Z");
          
          if (currentDate >= commutationStart && currentDate <= commutationEnd) {
            commutedAmount = parseFloat(commutation.amount) || 0;
          }
        }

        newTableData.push({
          monthYear,
          basicPay: parseFloat(basicPay),
          daRate: parseFloat(daRate),
          drawn: existingRow?.drawn || 0,
          arrears: existingRow?.arrears || 0,
          commuted: commutedAmount,
          // These will be calculated in the reactive block in ResultsView
          daAmount: 0,
          salaryDue: 0,
          difference: 0,
        });

        // Move to next month
        currentDate.setMonth(currentDate.getMonth() + 1);
      }

      // This creates the final calculation object for the view
      calculation = {
        id: calculation?.id, // Preserve existing ID
        ...formData,
        tableData: newTableData,
      };

      console.log(`Generated ${newTableData.length} rows for calculation`);
      showToast("Calculation completed successfully!", "success");

    } catch (error) {
      console.error("Error in handleCalculate:", error);
      showToast(`Calculation failed: ${error.message}`, "error");
    }
  }

  function handleSave(event) {
    // The form now holds the name and other primary data
    const formData = event.detail;

    if (!formData.name) {
      showToast(
        "Please provide a name for the calculation before saving.",
        "error"
      );
      return;
    }

    // Combine the form data with the (potentially edited) table data
    const finalCalculation = {
      ...calculation, // This has the table data
      ...formData, // This has the latest name, dates, etc.
    };

    saveCalculation(finalCalculation);
    
    // Update the current calculation with the saved data
    calculation = finalCalculation;
    
    // Show success message but stay on the current page
    showToast("Calculation saved successfully! You can continue editing.", "success");
  }
</script>

<section class="view active">
  <div class="view-header">
    <h3>
      {calculation ? "Editing: " + calculation.name : "New Salary Calculation"}
    </h3>
    <button
      class="btn-icon back-btn"
      title="Back to Home"
      aria-label="Back to Home"
      on:click={() => onNavigate("home")}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg
      >
    </button>
  </div>
  <div class="main-grid">
    <!-- The #key block correctly re-creates the form when the ID changes,
             ensuring it loads the correct initialData -->
    {#key calculationId}
      <CalculationForm
        on:calculate={handleCalculate}
        on:save={handleSave}
        initialData={calculation}
      />
    {/key}

    {#if calculation?.tableData}
      <ResultsView {calculation} />
    {/if}
  </div>
</section>
